import type { ReactNode } from "react";
import { Link, useParams } from "react-router";
import { HeaderLayoutView } from "src/common/components/layout/HeaderLayoutView.js";
import { nexusApi } from "src/common/service/nexus/nexusApi.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";

/**
 * Connected wrapper around `HeaderLayoutView`: derives the Domän/Bolag
 * breadcrumb trail from the URL, mirroring the sidebar's pickers, and wires
 * in the app's router `Link`. Used by every company-domain-scoped page
 * that isn't the dashboard itself.
 */
export const HeaderLayout = ({
	extraCrumbs = [],
	title,
	tabs,
	search,
	actions,
	children,
}: {
	/** Crumbs after the automatic Domän/Bolag trail — never pass the domain or entity crumbs. */
	extraCrumbs?: { name: string; path?: string | undefined }[];
	title: string;
	tabs?: ReactNode;
	search?: ReactNode;
	actions?: ReactNode;
	children: ReactNode;
}) => {
	const companyDomain = useLoadedCompanyDomain();
	const { legalEntityId } = useParams();
	const { data: legalEntities } =
		nexusApi.endpoints.getLegalEntities.useQueryState(companyDomain.id);
	const legalEntity = legalEntityId
		? legalEntities?.find((entity) => entity.id === legalEntityId)
		: undefined;

	return (
		<HeaderLayoutView
			breadcrumbs={[
				{
					label: companyDomain.name,
					href: `/company-domains/${companyDomain.id}`,
				},
				...(legalEntity
					? [
							{
								label: legalEntity.name,
								href: `/company-domains/${companyDomain.id}/legal-entities/${legalEntity.id}`,
							},
						]
					: []),
				...extraCrumbs.map((crumb) => ({
					label: crumb.name,
					href: crumb.path,
				})),
			]}
			title={title}
			tabs={tabs}
			search={search}
			actions={actions}
			renderLink={({ href, children: linkChildren }) => (
				<Link to={href}>{linkChildren}</Link>
			)}
		>
			{children}
		</HeaderLayoutView>
	);
};
